import React, { useState, useContext, useEffect } from 'react';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Tab, Stack, Paper, Button, Divider } from '@mui/material';
import { Archive, ArrowBackIosNew, RestoreFromTrash, Save } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useStyles } from '../Diocese.style';
import CreateEditDioceseForm, { DioceseValues } from './CreateEditDioceseForm';
import ConfirmationModal from 'Components/Reusable/Modals/ConfirmationModal';
import DonationTable, { DonationType } from 'Components/DonationTable';
import useDioceseActions from 'hooks/useDioceseActions';
import { GlobalContexts } from 'contexts/GlobalContext';
import LoadingButton from '@mui/lab/LoadingButton';
import { styles } from 'Components/Donors/DonorInfo/DonorInfo.style';
import { Diocese, StateType } from 'contexts/intialstates/Types';
import NotesDiocese from 'Components/AdminPanel/Diocese/NotesDiocese';
import useUserActions from 'hooks/useUserActions';

const CreateEditDioceseTab = () => {
	const { handleRoles } = useUserActions();
	const classes = useStyles();
	const navigate = useNavigate();
	const { id } = useParams();
	const [tabValue, setTabValue] = useState('overview');
	const [isDelete, setIsDelete] = useState(false);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [formData, setFormData] = useState<DioceseValues>({
		dioceseName: '',
		bishop: '',
		chancellor: '',
		streetLine1: '',
		streetline2: '',
		city: '',
		province: '',
		postalCode: '',
		country: '',
		contactName: '',
		phone: '',
		fax: '',
		email: '',
		language: '',
		useEmail: false,
		useEFile: false,
		magazine: false,
		code: undefined,
	});
	const [formErrors, setFormErrors] = useState<string[]>([]);
	const { deleteDiocese, restoreDiocese, updateDiocese, createDiocese } = useDioceseActions();
	const { setErrorSnack, setSimpleErrorSnack } = useContext(GlobalContexts);
	const { getDioceseByID } = useDioceseActions();
	const [reload, setReload] = useState(false);
	const [diocese, setDiocese] = React.useState<StateType<Diocese>>({
		data: null,
		error: null,
		loading: false,
	});
	const handleChange = (event: any, value: string) => {
		setTabValue(value);
	};

	const handleToggleDelete = () => {
		setIsDelete(!isDelete);
	};

	const handleDelete = async () => {
		if (!id) return;
		if (diocese.data?.archived) {
			setDeleteLoading(true);
			const isSucess = await restoreDiocese(parseInt(id));
			setDeleteLoading(false);
			handleToggleDelete();
			if (isSucess) {
				setReload(!reload);
				setErrorSnack(false);
			} else {
				setErrorSnack(true);
			}
			return;
		}
		setDeleteLoading(true);
		const isSucess = await deleteDiocese(parseInt(id));
		setDeleteLoading(false);
		handleToggleDelete();
		if (isSucess) {
			navigate('/admin/diocese');
			setErrorSnack(false);
		} else {
			setErrorSnack(true);
		}
	};

	const handleSubmit = async () => {
		const errors = [];
		if (!formData.dioceseName) {
			errors.push('dioceseName');
		}
		setFormErrors(errors);
		if (errors.length) {
			setSimpleErrorSnack({
				message: `Please fill all the required fields.`,
				severity: 'error',
				show: true,
			});
			return;
		}

		let isSucess;
		setLoading(true);
		if (id) {
			isSucess = await updateDiocese(formData, parseInt(id));
		} else {
			isSucess = await createDiocese(formData);
		}
		setLoading(false);
		if (isSucess) {
			navigate('/admin/diocese');
		}
	};
	useEffect(() => {
		if (!id) return;
		getDioceseByID({ setDiocese, diocese, id: parseInt(id) });
	}, [id, reload]);
	
	return (
		<Stack spacing={3}>
			<Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap={'wrap'}>
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="center"
					spacing={4}
					marginBottom={2}
				>
					<Stack className={classes.backIconWrapper}>
						<ArrowBackIosNew onClick={() => navigate('/admin/diocese')} />
					</Stack>
					<Stack direction="column" spacing={1}>
						<Stack alignItems={'flex-start'}>
							<span className={classes.title}>Diocese</span>
							<span className={classes.subTitle}></span>
							{id && (
								<Button sx={styles.statusBtn} disableElevation disableRipple variant="outlined">
									{diocese.data?.archived ? 'Archived' : 'Active'}
								</Button>
							)}
						</Stack>
					</Stack>
				</Stack>
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems={'center'}
					spacing={2}
					marginBottom={2}
				>
					{id && handleRoles('admin-actions') && (
						<Button
							sx={{ textTransform: 'none' }}
							startIcon={diocese.data?.archived ? <RestoreFromTrash /> : <Archive />}
							variant="contained"
							onClick={handleToggleDelete}
						>
							{diocese.data?.archived ? 'Unarchive' : 'Archive'}
						</Button>
					)}
					{tabValue === 'overview' && handleRoles('admin-actions') && (
						<LoadingButton
							sx={{ textTransform: 'none' }}
							startIcon={<Save />}
							variant="contained"
							loading={loading}
							loadingPosition="start"
							onClick={handleSubmit}
						>
							Save
						</LoadingButton>
					)}
				</Stack>
			</Stack>
			<Paper sx={{ padding: 2 }}>
				<TabContext value={tabValue}>
					<TabList onChange={handleChange}>
						<Tab label="Details" value="overview" />
						{id && <Tab label="Donation" value="donation" />}
						{id && <Tab label="Notes" value="notes" />}
					</TabList>
					<Divider />
					<TabPanel value="overview">
						<CreateEditDioceseForm
							initial={formData}
							setInitial={setFormData}
							formErrors={formErrors}
							setFormErrors={setFormErrors}
						/>
					</TabPanel>
					{id && (
						<TabPanel value="donation">
							<DonationTable id={parseInt(id)} type={DonationType.Diocese} />
						</TabPanel>
					)}
					{id && (
						<TabPanel value="notes">
							<NotesDiocese dioceseId={parseInt(id)} />
						</TabPanel>
					)}
				</TabContext>
			</Paper>
			{isDelete && (
				<ConfirmationModal
					handleClose={handleToggleDelete}
					message={
						diocese.data?.archived
							? 'Do you want to unarchive this Diocese'
							: 'Are you sure you want to archive this Diocese ?'
					}
					open={isDelete}
					handleConfirmation={handleDelete}
					handleCancel={handleToggleDelete}
					loading={deleteLoading}
				/>
			)}
		</Stack>
	);
};

export default CreateEditDioceseTab;
